import { MarkdownReact } from "@/components/MarkdownReact";
import type { ComponentProps } from "react";
import { PROSE_INDENT_CLASS } from "./proseIndent";
import { remarkHardBreaks } from "./remarkHardBreaks";
import type { FpCommonProps } from "./types";

/** Default prose styling for markdown blocks, replaced by an authored class. */
export const DEFAULT_MARKDOWN_CLASS = `prose prose-xs prose-slate max-w-none text-gray-800 text-xs [font-synthesis:style] [&_li::marker]:text-black [&_ul_ul]:list-[circle] ${PROSE_INDENT_CLASS}`;

interface MarkdownProseProps extends FpCommonProps {
  markdown: string;
  "data-fp-text-start"?: number;
  "data-fp-text-end"?: number;
  /** Hover tooltip naming the bound field, shown in the editor. */
  fieldTitle?: string;
  /** How an authored class combines with the default prose class. */
  classMode?: "replace" | "extend";
  /** Extra remark plugins (e.g. field-token resolution) passed to the renderer. */
  extraRemarkPlugins?: ComponentProps<
    typeof MarkdownReact
  >["extraRemarkPlugins"];
  /** Component overrides (e.g. unit-marker stamping) passed to the renderer. */
  components?: ComponentProps<typeof MarkdownReact>["components"];
}

/**
 * Block-level markdown shell for `<fp-markdown>` and markdown `<fp-field>` values.
 * Single newlines are hard breaks: authored document blocks are laid out line by
 * line, so an address written on five lines must print on five lines.
 * @param props - The markdown text, class-combination mode, and common fp-* props
 * @returns The rendered markdown block
 */
export function MarkdownProse({
  markdown,
  className,
  classMode = "replace",
  style,
  extraRemarkPlugins,
  components,
  fieldTitle,
  "data-fp-node": dataFpNode,
  "data-fp-text-start": textStart,
  "data-fp-text-end": textEnd,
}: MarkdownProseProps) {
  const combinedClass =
    className === undefined
      ? DEFAULT_MARKDOWN_CLASS
      : classMode === "extend"
        ? `${DEFAULT_MARKDOWN_CLASS} ${className}`
        : className;
  return (
    <div
      className={combinedClass}
      style={style}
      data-fp-node={dataFpNode}
      data-fp-text-start={textStart}
      data-fp-text-end={textEnd}
      data-field-title={fieldTitle}
    >
      <MarkdownReact
        extraRemarkPlugins={[...(extraRemarkPlugins ?? []), remarkHardBreaks]}
        components={components}
      >
        {markdown}
      </MarkdownReact>
    </div>
  );
}
